import { Person, PersonDisplayProperties } from "./ResearchDataModel"
import { useAssertionStore } from './AssertionStore'
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import _, { uniqBy } from "lodash"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { HistoricalDate } from './HistoricalDate'
import { SortDate } from './SortDate'
import { usePlaceStore } from './PlaceStore'
import { plainToSignedUrl } from './ContentStore'
import { useContentTagStore } from './ContentTagStore'

// NOTE: All cached persons are expected to have display properties
const personExpand = "displayProperties"

export const usePersonStore = defineStore("personStore", () => {
    const asyncPersons = new Map<string, AsyncData<Person>>()
    const asyncAssertionKeys = new Map<string, AsyncData<VersionedKeyList>>()
    const asyncContentTagKeys = new Map<string, AsyncData<VersionedKeyList>>()

    const rd = useResearchDataApi()
    const assertionStore = useAssertionStore()
    const placeStore = usePlaceStore()
    const contentTagStore = useContentTagStore()

    function getLoadedPersonList(ids: (string | undefined)[]) {
        return getAsyncPersonList(ids).map(a => a.data).filter(isDefined)
    }

    function getAsyncPerson(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncPersonList([id], loadMode)[0] : undefined
    }

    function getAsyncPersonList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = usePersonStore() // use to get/return reactive store objects
        const loadPersonsAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainPersons = await rd.getPlainByIdsAsync("persons", idsToLoad, personExpand)
            return processExpandedPersons(plainPersons, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncPersons, ids, "Person", loadMode, loadPersonsAsync)
    }

    async function getPersonListAsync(ids: (string | undefined)[]) {
        const asyncItems = getAsyncPersonList(ids, LoadMode.EnsureLoaded)
        return (await Promise.all(asyncItems.map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    function getAsyncAssertionKeyLists(personIds: string[], loadMode = LoadMode.TrackOnly) {
        if (loadMode != LoadMode.TrackOnly)
            throw new Error(`PersonStore: Load mode not implemented`)

        // ensure all requested key lists are present
        const store = usePersonStore() // use to get/return reactive store objects
        AsyncDataStore.getAsyncItemsToLoad(store.asyncAssertionKeys, personIds, "Person.AssertionKeys", loadMode)
        // get all key lists (not just the ones that need loading)
        return personIds.map(id => store.asyncAssertionKeys.get(id)!)
    }

    function getLoadedAssertionKeyLists(personIds: string[]) {
        const store = usePersonStore() // use to get/return reactive store objects
        return personIds.map(id => store.asyncAssertionKeys.get(id)?.data).filter(isDefined)
    }

    function areDetailsLoaded(personId: string) {
        const store = usePersonStore() // use to get/return reactive store objects
        const p = store.asyncPersons.get(personId)
        if (!p || !p.isLoadComplete())
            return false

        const asyncAssertionKeyList = store.asyncAssertionKeys.get(personId)
        if (!asyncAssertionKeyList)
            return false

        const assertionIds = asyncAssertionKeyList.data?.keys ?? []
        return assertionStore.areExpandedAssertionsLoaded(assertionIds)
    }

    async function loadDetailsAsync(personId: string, loadMode = LoadMode.TrackOnly) {
        if (loadMode != LoadMode.Reload && areDetailsLoaded(personId)) {
            console.log(`Details for person ${personId} already loaded`)
            return
        }
        if (loadMode != LoadMode.TrackOnly) {
            const expand = "displayProperties,assertions/place,assertions/site"
            const requestTime = DateTime.utc()
            const plainPersons = await rd.getPlainByIdsAsync("persons", [personId], expand)

            processExpandedPersons(plainPersons, requestTime)
        }
    }

    async function addPlaceholderAsync(groupId: string) {
        const store = usePersonStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await rd.postPlainAsync(`groups/${groupId}/persons`)
            return plainToPerson(p)
        }            
        return await AsyncDataStore.addToStoreMapAsync(store.asyncPersons, "Person", ps => ps.id, addCoreAsync)
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function addPersonsToStore(plainPersons: any[], requestTime: DateTime) {
        if (plainPersons.some(p => !p.displayProperties))
            throw 'All persons added to store should have display properties'

        const store = usePersonStore() // use to get/return reactive store objects
        const personMap = processExpandedPersons(uniqBy(plainPersons, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncPersons, "Person", personMap, requestTime)
    }

    function processExpandedPersons(plainPersons: any[], requestTime: DateTime) {
        processExpandedPersonDisplayProperties(plainPersons.map(p => p.displayProperties), requestTime)

        const assertionMap = new Map(plainPersons
            .filter(p => p.assertions) // only if assertion list is present
            .map(p => [p.id as string, p.assertions as any[]]))
        processExpandedPersonAssertions(assertionMap, requestTime)

        const tagMap = new Map(plainPersons
            .filter(p => p.contentTags)
            .map(p => [p.id as string, p.contentTags as any[]]))
        processExpandedPersonContentTags(tagMap, requestTime)
        
        return AsyncDataStore.createItemMap(plainPersons, "Person", plainToPerson, p => p.id!)
    }

    function processExpandedPersonDisplayProperties(plainDisplayProperties: any[], requestTime: DateTime) {
        placeStore.addPlacesToStore(plainDisplayProperties.map(p => p.birthPlace).filter(isDefined), requestTime)
        placeStore.addPlacesToStore(plainDisplayProperties.map(p => p.deathPlace).filter(isDefined), requestTime)
    }

    function processExpandedPersonAssertions(plainAssertionMap: Map<string, any[]>, requestTime: DateTime) {
        const store = usePersonStore() // use to get/return reactive store objects
        for (const e of plainAssertionMap.entries()) { // e = [personId, plainAssertions]
            const assertionMap = assertionStore.addAssertionsToStore(e[1], requestTime)
            const assertionIds = [...assertionMap.values()].map(a => a.id!)

            AsyncDataStore.addKeyListToStore(store.asyncAssertionKeys, e[0], "Person.AssertionKeys", assertionIds, requestTime)
        }
    }

    function processExpandedPersonContentTags(plainTagMap: Map<string, any[]>, requestTime: DateTime) {
        const store = usePersonStore() // use to get/return reactive store objects
        for (const e of plainTagMap.entries()) { // e = [personId, plainTags]
            const tagMap = contentTagStore.addTagsToStore(e[1], requestTime)
            const tagIds = [...tagMap.values()].map(t => t.id!)

            AsyncDataStore.addKeyListToStore(store.asyncContentTagKeys, e[0], "Person.ContentTagKeys", tagIds, requestTime)
        }
    }

    return {
        asyncPersons,
        asyncAssertionKeys,
        asyncContentTagKeys,

        getLoadedPersonList,
        getAsyncPerson,
        getAsyncPersonList,
        getPersonListAsync,
        getAsyncAssertionKeyLists,
        getLoadedAssertionKeyLists,
        areDetailsLoaded,
        loadDetailsAsync,
        addPlaceholderAsync,

        addPersonsToStore,
        processExpandedPersons,
        processExpandedPersonDisplayProperties,
        processExpandedPersonAssertions,
        processExpandedPersonContentTags,
    }
})

export function plainToPerson(p: any) {
    const person = assignExisting(new Person(), p)
    person.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate);
    person.profile = p.profile ?? false
    person.placeholder = p.placeholder ?? false
    person.displayProperties = plainToPersonDisplayProperties(p.displayProperties ?? {})
    return person
}

export function plainToPersonDisplayProperties(p: any) {
    const dp = assignExisting(new PersonDisplayProperties(), p)
    
    dp.birthDate = HistoricalDate.parse(p.birthDate)
    dp.birthSortDate = SortDate.parse(p.birthSortDate)
    dp.birthPlaceId = p.birthPlace?.id

    dp.deathDate = HistoricalDate.parse(p.deathDate)
    dp.deathSortDate = SortDate.parse(p.deathSortDate)
    dp.deathPlaceId = p.deathPlace?.id

    dp.primaryPhotoSignedUrl = plainToSignedUrl(p.primaryPhotoSignedUrl)
    return dp
}
